import { useCallback, useEffect, useRef, useState } from 'react'
import {
  listEventCaptainRoster,
  listGroupEventsNeedingCaptains,
  listMyCaptainSignups,
  type EventNeedingCaptains,
  type EventRosterEntry,
  type MyCaptainSignup,
} from './captainApi'
import type { CaptainEvent, CaptainRole, RoleSlot } from '../types/captain'

export interface RosterEntry {
  role: CaptainRole
  displayName: string | null
  signedUpAt: string | null
  signupId: string
}

interface Args {
  groupId: string | undefined
  /** When true, also fetch per-event rosters via list_event_captain_roster. */
  isOrganizer: boolean
  /** Auth user id; used to mark the user's own slot in event.slots.filledBy. */
  userId: string | undefined
  /** When false, the hook skips all fetching (e.g. mock mode). */
  enabled: boolean
}

export interface CaptainPortalData {
  upcomingEvents: CaptainEvent[]
  pastEvents: CaptainEvent[]
  /** Maps event_id → the caller's signup_id for that event (when they're signed up). */
  mySignupIdByEventId: Record<string, string>
  /** Maps event_id → the full roster (organizer view only). */
  rostersByEventId: Record<string, RosterEntry[]>
  loading: boolean
  /** True once the very first fetch has settled (succeeded or failed). */
  hasLoaded: boolean
  error: string | null
  refetch: () => Promise<void>
}

/** Sentinel placeholder for slots filled by other users — never compared against a real user id. */
const FILLED_PLACEHOLDER = '__filled__'

function adaptEvent(
  rpc: EventNeedingCaptains,
  myUserId: string | undefined,
  myRole: CaptainRole | undefined
): CaptainEvent {
  const slots: RoleSlot[] = rpc.roles.map((r) => {
    const filledBy: string[] = Array<string>(r.filled).fill(FILLED_PLACEHOLDER)
    if (myUserId && myRole === r.role && filledBy.length > 0) {
      filledBy[0] = myUserId
    }
    return { role: r.role, target: r.slots, filledBy }
  })
  return {
    id: rpc.event_id,
    name: rpc.title,
    date: rpc.event_datetime,
    location: rpc.start_location_name ?? '',
    needsCaptains: true,
    slots,
  }
}

function adaptPastSignupToEvent(s: MyCaptainSignup, myUserId: string): CaptainEvent {
  return {
    id: s.event_id,
    name: s.event_title,
    date: s.event_datetime,
    location: s.start_location_name ?? '',
    needsCaptains: true,
    slots: [
      { role: s.role, target: 1, filledBy: [myUserId] },
    ],
  }
}

export function useCaptainPortalData({
  groupId,
  isOrganizer,
  userId,
  enabled,
}: Args): CaptainPortalData {
  const [upcomingEvents, setUpcomingEvents] = useState<CaptainEvent[]>([])
  const [pastEvents, setPastEvents] = useState<CaptainEvent[]>([])
  const [mySignupIdByEventId, setMySignupIdByEventId] = useState<Record<string, string>>({})
  const [rostersByEventId, setRostersByEventId] = useState<Record<string, RosterEntry[]>>({})
  const [loading, setLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Monotonic request id — only the latest fetch is allowed to apply state.
  // Prevents StrictMode's double-mount + post-mutation refetch from racing
  // and clobbering fresh data with stale snapshots.
  const requestIdRef = useRef(0)

  const fetchAll = useCallback(async () => {
    if (!enabled || !groupId) return
    const myRequestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    try {
      const [eventsRpc, signupsRpc] = await Promise.all([
        listGroupEventsNeedingCaptains(groupId),
        listMyCaptainSignups(),
      ])

      // Filter the cross-group list_my_captain_signups payload to just this
      // club, and drop any cancelled rows — the RPC can return them, but the
      // UI should treat the user as not signed up for that event.
      const mySignupsInGroup = signupsRpc.filter(
        (s) => s.group_id === groupId && s.status === 'confirmed'
      )

      const myRoleByEventId: Record<string, CaptainRole> = {}
      const mySignupIds: Record<string, string> = {}
      for (const s of mySignupsInGroup) {
        myRoleByEventId[s.event_id] = s.role
        mySignupIds[s.event_id] = s.signup_id
      }

      const upcoming: CaptainEvent[] = eventsRpc.map((e) =>
        adaptEvent(e, userId, myRoleByEventId[e.event_id])
      )

      const past: CaptainEvent[] = mySignupsInGroup
        .filter((s) => s.bucket === 'past')
        .map((s) => adaptPastSignupToEvent(s, userId ?? ''))

      // Organizer rosters: fetch per-event in parallel. Use allSettled so a
      // single failing event doesn't blank out the entire organizer view —
      // a partial roster is more useful than a hard error screen.
      let rostersMap: Record<string, RosterEntry[]> = {}
      if (isOrganizer && eventsRpc.length > 0) {
        const rosterResults = await Promise.allSettled(
          eventsRpc.map((e) =>
            listEventCaptainRoster(e.event_id).then(
              (entries) => [e.event_id, entries] as const
            )
          )
        )
        for (const result of rosterResults) {
          if (result.status === 'fulfilled') {
            const [eventId, entries] = result.value
            rostersMap[eventId] = entries.map((r: EventRosterEntry) => ({
              role: r.role,
              displayName: r.display_name,
              signedUpAt: r.created_at,
              signupId: r.signup_id,
            }))
          } else {
            console.warn('[captain] roster fetch failed for one event', result.reason)
          }
        }
      }

      // Discard the result if a newer fetch was dispatched while we were in
      // flight. Without this guard, a slow stale fetch can overwrite the
      // post-mutation snapshot with pre-mutation data.
      if (myRequestId !== requestIdRef.current) return

      setUpcomingEvents(upcoming)
      setPastEvents(past)
      setMySignupIdByEventId(mySignupIds)
      setRostersByEventId(rostersMap)
    } catch (e) {
      if (myRequestId !== requestIdRef.current) return
      const msg = (e as { message?: string })?.message ?? 'Could not load events.'
      setError(msg)
    } finally {
      // Only the latest fetch updates loading/hasLoaded — otherwise an early
      // stale fetch finishing late would flip loading=false prematurely.
      if (myRequestId === requestIdRef.current) {
        setLoading(false)
        setHasLoaded(true)
      }
    }
  }, [enabled, groupId, isOrganizer, userId])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return {
    upcomingEvents,
    pastEvents,
    mySignupIdByEventId,
    rostersByEventId,
    loading,
    hasLoaded,
    error,
    refetch: fetchAll,
  }
}
